import {
  AddDocumentCommentDto,
  AddDocumentDto,
  DocumentEntity,
  DocumentEditorResponse,
  DocumentAnnotation,
  DocumentAnnotationHistory,
  DocumentAnnotationGroup,
  DocumentMetaType,
  DocumentSearchParams,
  EditDocumentDto,
  InviteDocumentGuestDto,
  InviteDocumentGuestResponse,
  ReplaceDocumentResponse,
  ReplaceDocumentData,
  SignDocumentDto,
  UpdateEditorDocumentMetadataDto,
  UpdateDocumentSignaturesBody,
} from '@/api';
import API from '@api/API';
import { ENDPOINTS } from '../endpoints';

export const documentsApi = {
  getDocument: async (id: string, documentId: string): Promise<{ document: DocumentEntity }> => {
    const response = await API().get(ENDPOINTS.documents.one(id, documentId));
    return response.data;
  },

  getDocumentBacklinks: async (
    id: string,
    params?: DocumentSearchParams,
  ): Promise<DocumentEntity[]> => {
    const response = await API().get(ENDPOINTS.documents.withBacklinks(id), {
      params,
    });
    return response.data?.documents;
  },

  getDeletedDocuments: async (
    id: string,
    params?: DocumentSearchParams,
  ): Promise<DocumentEntity[]> => {
    const response = await API().get(ENDPOINTS.documents.allDeleted(id), {
      params,
    });
    return response.data?.documents;
  },

  addDocument: async (auditId: string, document: AddDocumentDto): Promise<void> => {
    const response = await API().postForm(ENDPOINTS.documents.create(auditId), document);
    return response.data;
  },

  addMultipleDocuments: async (auditId: string, data: FormData): Promise<DocumentEntity[]> => {
    const response = await API().postForm(ENDPOINTS.documents.createMultiple(auditId), data);
    return Array.isArray(response.data) ? response.data : response.data?.documents || [];
  },

  updateDocument: async (
    auditId: string,
    documentId: string | number,
    document: EditDocumentDto,
  ): Promise<void> => {
    const response = await API().postForm(
      ENDPOINTS.documents.update(auditId, documentId),
      document,
    );
    return response.data;
  },

  deleteDocument: async (auditId: string, documentId: string | number): Promise<void> => {
    const response = await API().delete(ENDPOINTS.documents.delete(auditId, documentId));
    return response.data;
  },

  /**
   * Every field is optional (`ReplaceDocumentResponse`); a body that is not an object is
   * an answer with nothing to apply.
   */
  replaceDocument: async (
    auditId: string,
    documentId: string | number,
    data: ReplaceDocumentData,
  ): Promise<ReplaceDocumentResponse> => {
    const response = await API().postForm(ENDPOINTS.documents.replace(auditId, documentId), data);
    const body: unknown = response.data;
    return body && typeof body === 'object' ? (body as ReplaceDocumentResponse) : {};
  },

  editDocument: async (
    auditId: string,
    documentId: string | number,
    data: EditDocumentDto,
  ): Promise<void> => {
    const response = await API().postForm(ENDPOINTS.documents.update(auditId, documentId), data);
    return response.data;
  },

  updateDocumentSignatures: async (
    auditId: string,
    documentId: string | number,
    data: UpdateDocumentSignaturesBody,
  ): Promise<void> => {
    const response = await API().post(
      ENDPOINTS.documents.updateSignatures(auditId, documentId),
      data,
    );
    return response.data;
  },

  signDocument: async (
    auditId: string,
    documentId: string | number,
    data: SignDocumentDto,
  ): Promise<void> => {
    const response = await API().post(ENDPOINTS.documents.sign(auditId, documentId), data);
    return response.data;
  },

  downloadDocument: async (
    auditId: string,
    documentId: string | number,
  ): Promise<string | undefined> => {
    const response = await API().get(ENDPOINTS.documents.download(auditId, documentId));
    return response.data;
  },

  addDocumentComments: async (
    auditId: string,
    documentId: string | number,
    data: AddDocumentCommentDto,
  ): Promise<void> => {
    const response = await API().post(ENDPOINTS.documents.comment(auditId, documentId), data);
    return response.data;
  },

  inviteDocumentGuest: async (
    auditId: string,
    documentId: string | number,
    data: InviteDocumentGuestDto,
  ): Promise<InviteDocumentGuestResponse> => {
    const response = await API().post(ENDPOINTS.documents.inviteGuest(auditId, documentId), data);
    return response.data;
  },

  restoreDocument: async (auditId: string, documentId: string | number): Promise<void> => {
    const response = await API().post(ENDPOINTS.documents.restore(auditId, documentId));
    return response.data;
  },

  verifySwisscom: async (auditId: string, documentId: string | number): Promise<void> => {
    const response = await API().post(ENDPOINTS.documents.verifySwisscom(auditId, documentId));
    return response.data;
  },

  syncDocument: async (auditId: string, documentId: string | number): Promise<void> => {
    const response = await API().get(ENDPOINTS.documents.sync(auditId, documentId));
    return response.data;
  },

  // TODO move to bexio api
  exportDocument: async (auditId: string, documentId: string | number): Promise<void> => {
    const response = await API().post(ENDPOINTS.bexio.export(auditId, documentId));
    return response.data;
  },

  refetchDocument: async (auditId: string, documentId: string | number): Promise<void> => {
    const response = await API().post(ENDPOINTS.bexio.refetch(auditId, documentId));
    return response.data;
  },

  getDocumentEditor: async (
    auditId: string,
    documentId: string | number,
  ): Promise<DocumentEditorResponse> => {
    const response = await API().get(ENDPOINTS.documents.editor(auditId, documentId));
    return response.data;
  },

  createAnnotation: async (
    auditId: string,
    documentId: string | number,
    data: Partial<DocumentAnnotation>,
  ): Promise<{ annotation: DocumentAnnotation }> => {
    const response = await API().post(
      ENDPOINTS.documents.editorAnnotations(auditId, documentId),
      data,
    );
    return response.data;
  },

  updateAnnotation: async (
    auditId: string,
    documentId: string | number,
    annotationId: string | number,
    data: Partial<DocumentAnnotation>,
  ): Promise<{ annotation: DocumentAnnotation }> => {
    const response = await API().patch(
      ENDPOINTS.documents.editorAnnotationItem(auditId, documentId, annotationId),
      data,
    );
    return response.data;
  },

  deleteAnnotation: async (
    auditId: string,
    documentId: string | number,
    annotationId: string | number,
  ): Promise<{ success: boolean }> => {
    const response = await API().delete(
      ENDPOINTS.documents.editorAnnotationItem(auditId, documentId, annotationId),
    );
    return response.data;
  },

  bulkAnnotations: async (
    auditId: string,
    documentId: string | number,
    data: {
      create?: Array<Partial<DocumentAnnotation>>;
      update?: Array<Partial<DocumentAnnotation>>;
      remove?: Array<number>;
    },
  ): Promise<{
    created: DocumentAnnotation[];
    updated: DocumentAnnotation[];
    removed: number[];
  }> => {
    const response = await API().post(
      ENDPOINTS.documents.editorAnnotationsBulk(auditId, documentId),
      data,
    );
    return response.data;
  },

  getAnnotationHistory: async (
    auditId: string,
    documentId: string | number,
  ): Promise<{ history: DocumentAnnotationHistory[] }> => {
    const response = await API().get(ENDPOINTS.documents.editorHistory(auditId, documentId));
    return response.data;
  },

  updateWorkPerformed: async (
    auditId: string,
    documentId: string | number,
    workPerformed: string,
  ): Promise<{ document: { id: number; workPerformed: string | null } }> => {
    const response = await API().patch(
      ENDPOINTS.documents.editorWorkPerformed(auditId, documentId),
      { workPerformed },
    );
    return response.data;
  },

  updateEditorMetadata: async (
    auditId: string,
    documentId: string | number,
    data: UpdateEditorDocumentMetadataDto,
  ): Promise<{ document: DocumentEntity }> => {
    const response = await API().patch(
      ENDPOINTS.documents.editorMetadata(auditId, documentId),
      data,
    );
    return response.data;
  },

  createAnnotationGroup: async (
    auditId: string,
    documentId: string | number,
  ): Promise<{ group: DocumentAnnotationGroup }> => {
    const response = await API().post(ENDPOINTS.documents.editorGroups(auditId, documentId));
    return response.data;
  },

  getDocumentTypes: async (auditId: string): Promise<{ documentTypes: DocumentMetaType[] }> => {
    const response = await API().get(ENDPOINTS.documentTypes.list(auditId));
    return response.data;
  },

  createDocumentType: async (
    auditId: string,
    title: string,
    color?: string | null,
  ): Promise<{ documentType: DocumentMetaType }> => {
    const response = await API().post(ENDPOINTS.documentTypes.create(auditId), { title, color });
    return response.data;
  },

  updateDocumentType: async (
    auditId: string,
    documentTypeId: string | number,
    data: { title?: string; color?: string | null },
  ): Promise<{ documentType: DocumentMetaType }> => {
    const response = await API().patch(
      ENDPOINTS.documentTypes.update(auditId, documentTypeId),
      data,
    );
    return response.data;
  },

  deleteDocumentType: async (
    auditId: string,
    documentTypeId: string | number,
  ): Promise<{ success: boolean }> => {
    const response = await API().delete(ENDPOINTS.documentTypes.delete(auditId, documentTypeId));
    return response.data;
  },
};
